/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { LoggerServiceFactory } from '../../logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { catchError, filter, map, mapTo, tap } from 'rxjs/operators';
import { LocalStorageHelper } from 'src/app/plain-javascript/services/LocalStorageHelper';
import { CanActivateFn } from '@angular/router';
import { LoadingBlockerState } from '../../utility-components/loading-blocker';
import { AuthService } from 'src/app/services/auth.service';
import { from, of, Subject } from 'rxjs';
import { Tenant } from '@touchplan/common';
import { tenantGetterService } from '@touchplan/firebase-common';
import { IAccessCodeData } from '../../rally-point/rally-types';
import { fancyDatabase } from '@touchplan/client-firebase';

//a test route guard for requiring an email in the store
export const emailRequiredInStoreGuard: CanActivateFn = (activatedRouteSnapshot, state) => {
	const accountCreationStore = inject(AccountCreationStore);

	return accountCreationStore.email$.pipe(map((email) => !!email));
};

//making this a guard for earlier loading
export const resolveTenantFromUrl: CanActivateFn = (route, state) => {
	const accountCreationStore = inject(AccountCreationStore);
	// const authService = inject(AuthService);

	const rtdbTenantId = route.queryParams['companyId'];
	if (rtdbTenantId) {
		return from(tenantGetterService.getTenantViaRtdbId(fancyDatabase, rtdbTenantId)).pipe(
			catchError((e) => {
				console.log('caught error resolving tenant', e);
				return of(null as Tenant);
			}),
			tap((tenant) => {
				//skip as many superfluous emissions onto the store as possible
				if (tenant) {
					accountCreationStore.setTenant(tenant);
				}
			}),
			mapTo(true)
		);
	}
	return of(true);
};

//TODO - seperate file maybe
export interface IAccountCreationStore {
	email?: string; //localStorage'd
	password?: string;
	firstName?: string;
	lastName?: string;
	company?: string;
	phone?: string;
	title?: string;
	accessCode?: string; //despite being a number, the existing rally point stuff using a text input
	accessCodeData?: IAccessCodeData;
	tenant?: Tenant;
	loadingState?: LoadingBlockerState | boolean;
}

type ISignupData = Required<
	Pick<IAccountCreationStore, 'firstName' | 'lastName' | 'company' | 'phone' | 'title'>
>;
type IWriteableSignupData = Pick<
	IAccountCreationStore,
	'email' | 'firstName' | 'lastName' | 'company' | 'phone' | 'title' | 'accessCode'
>;

// ideally this service would be component provided (and thus self-destruct/ isolate) but then the router
// can't find it. And the router finding it is pretty core to the entire point of having a store.
@Injectable({ providedIn: 'root' })
export class AccountCreationStore extends ComponentStore<IAccountCreationStore> {
	private static get emailFromLocalStorage() {
		return LocalStorageHelper.read<string>(localStoragePrefix + 'email');
	}

	private static get accessCodeFromLocalStorage() {
		return LocalStorageHelper.read<string>(localStoragePrefix + 'accessCode');
	}

	private static get initialState() {
		return {
			email: AccountCreationStore.emailFromLocalStorage,
			accessCode: AccountCreationStore.accessCodeFromLocalStorage,
			loadingState: LoadingBlockerState.empty,
		};
	}

	private logger: ILoggerService = LoggerServiceFactory.create('AccountCreationStore');

	/** A suite of data corresponding to the state of the current unauthenticated/ incomplete user */
	public accountData$ = this.select((state) => state);

	public email$ = this.select((state) => state.email);

	public loadingState$ = this.select((state) => state.loadingState);

	/** This is only a valid source of the tenant when on any of the auth routes */
	public tenant$ = this.select((state) => state.tenant);

	/** user is sso */
	public isSSO$ = this.select(
		(state) =>
			!!state.tenant?.tenantMetadata?.providerId && state.tenant?.tenantMetadata?.providerId !== 'password'
	);

	public accessCode$ = this.select((state) => state.accessCode);

	public accessCodeData$ = this.select((state) => state.accessCodeData);

	// We need a subject to kind of "simulate" an onDestroy life cycle hook because we cannot access the component store ones since this is not a component
	// We call the nukeState method on the emptyAccountCreationStoreGuard that its "hooked" to the parent canDeactivate
	private nukeSubject$ = new Subject<void>();
	public nukeObs$ = this.nukeSubject$.asObservable();

	//On Create
	constructor(logger: LoggerServiceFactory, private authService: AuthService) {
		super(AccountCreationStore.initialState); //initial state
		this.persistEmailChangesToLocalStorage$(this.email$);
		this.persistAccessCodeChangesToLocalStorage$(this.accessCode$);
		this.applyTenantToAuth$(this.tenant$);
	}

	/** slap the email onto the state, for the login page */
	public readonly collectEmail = this.updater((state, email: string) => {
		return { ...state, email };
	});

	public readonly setTenant = this.updater((state, tenant: Tenant) => {
		return { ...state, tenant };
	});

	/** specific writer for the signup page "appropriately" typed */
	public readonly collectSignupData = this.updater((state, signupData: ISignupData) => {
		return { ...state, ...signupData };
	});

	/** for use by the router and other behind the scenes things */
	public readonly updateEntireState = this.updater((state, newState: IAccountCreationStore) => {
		return { ...state, ...newState };
	});

	/** for destroying the data in the state when navigating away */
	private readonly _nukeState = this.updater(() => {
		//need to specifically null the email as an override in this case

		return { ...AccountCreationStore.initialState, email: null, accessCode: null, accessCodeData: null };
	});

	public nukeState() {
		//a "pre-nuke" event
		this.nukeSubject$.next();
		this._nukeState();
	}

	/** for setting the loading state */
	public readonly setLoadingState = this.updater((state, loadingState: LoadingBlockerState | boolean) => {
		return { ...state, loadingState };
	});

	/** for setting the password */
	public updatePassword = this.updater((state, password: string) => {
		return { ...state, password };
	});

	public setAccessCode = this.updater((state, accessCode: string) => {
		return { ...state, accessCode };
	});

	public setAccessCodeData = this.updater((state, accessCodeData: IAccessCodeData) => {
		return { ...state, accessCodeData };
	});

	/**
	 * trims the entire set of data in this store to just the data intended to be writable to firebase
	 * Seems like questionable value to expose a listener for that purpose explicitly.
	 */
	public filterToWritableData(fullData: IAccountCreationStore): IWriteableSignupData {
		return Object.entries(fullData).reduce((compiledObject, currentValue) => {
			if (writableKeysIndex[currentValue[0]]) {
				compiledObject[currentValue[0]] = currentValue[1];
			}
			return compiledObject;
		}, {});
	}

	// (sync) NOT to be called on load, only when we need it
	public refreshLocalStorage = this.effect<void>((trigger$) => {
		return trigger$.pipe(
			//can't really think of any particularly good reason to store stuff other than email
			map(() => [
				AccountCreationStore.emailFromLocalStorage,
				AccountCreationStore.accessCodeFromLocalStorage,
			]),
			filter(([email, accessCode]) => !!email || !!accessCode),
			//oh hey, the type inference of tapResponse is broken, that's fun
			tapResponse<string[]>(
				([email, accessCode]) => {
					if (!!email) {
						this.collectEmail(email);
					}
					if (!!accessCode) {
						this.setAccessCode(accessCode);
					}
				},
				(error) => {
					this.logger.log(error);
				}
			)
		);
	});

	//writes to localStorage on email change
	private persistEmailChangesToLocalStorage$ = this.effect<string>((email$) => {
		return email$.pipe(
			tapResponse<string>(
				(storedEmail) => {
					if (storedEmail) {
						LocalStorageHelper.write(localStoragePrefix + 'email', storedEmail);
					} else {
						LocalStorageHelper.remove(localStoragePrefix + 'email');
					}
				},
				(error) => {
					this.logger.error(error);
				}
			)
		);
	});

	//writes to localStorage on accessCode change
	private persistAccessCodeChangesToLocalStorage$ = this.effect<string>((accessCode$) => {
		return accessCode$.pipe(
			tapResponse<string>(
				(storedAccessCode) => {
					if (storedAccessCode) {
						LocalStorageHelper.write(localStoragePrefix + 'accessCode', storedAccessCode);
					} else {
						LocalStorageHelper.remove(localStoragePrefix + 'accessCode');
					}
				},
				(error) => {
					this.logger.error(error);
				}
			)
		);
	});

	private applyTenantToAuth$ = this.effect<Tenant>((tenant$) => {
		return tenant$.pipe(
			//we never actual change the tenant in a session so nulls can be ignored
			filter((tenant) => !!tenant),
			tapResponse<Tenant>(
				(tenant) => {
					this.authService.tenant = tenant;
				},
				(error) => {
					this.logger.error(error);
				}
			)
		);
	});
}

const localStoragePrefix = 'acc-';
const writableKeys = ['email', 'firstName', 'lastName', 'company', 'phone', 'title', 'accessCode'];
const writableKeysIndex: { [key: string]: true } = writableKeys.reduce((obj, curr) => {
	obj[curr] = true;
	return obj;
}, {});
